import { deepQuerySelector } from "./theme.js";

/**
 * custom.js
 * Place your custom web components below.
 */

var __typeError = (msg) => {
  throw TypeError(msg);
};
var __accessCheck = (obj, member, msg) =>
  member.has(obj) || __typeError("Cannot " + msg);
var __privateGet = (obj, member, getter) => (
  __accessCheck(obj, member, "read from private field"),
  getter ? getter.call(obj) : member.get(obj)
);
var __privateAdd = (obj, member, value) =>
  member.has(obj)
    ? __typeError("Cannot add the same private member more than once")
    : member instanceof WeakSet
    ? member.add(obj)
    : member.set(obj, value);
var __privateSet = (obj, member, value, setter) => (
  __accessCheck(obj, member, "write to private field"),
  setter ? setter.call(obj, value) : member.set(obj, value),
  value
);
var __privateMethod = (obj, member, method) => (
  __accessCheck(obj, member, "access private method"), method
);

// js/common/product/variant-pack-selector.js
import { Delegate as Delegate4 } from "vendor";
var CACHE_EVICTION_TIME = 1e3 * 60 * 5;
var _preloadedHtml,
  _delegate,
  _intersectionObserver,
  _form,
  _selectedVariant,
  _VariantPicker_instances,
  getActiveOptionValues_fn,
  getOptionValuesFromOption_fn,
  onOptionChanged_fn,
  onOptionPreload_fn,
  onIntersection_fn,
  renderForCombination_fn,
  createHashKeyForHtml_fn;
var _VariantPicker = class _VariantPicker extends HTMLElement {
  constructor() {
    super(...arguments);
    console.log("DEBUG - Constructed");
    __privateAdd(this, _VariantPicker_instances);
    __privateAdd(this, _delegate, new Delegate4(document.body));
    __privateAdd(
      this,
      _intersectionObserver,
      new IntersectionObserver(
        __privateMethod(this, _VariantPicker_instances, onIntersection_fn).bind(
          this
        )
      )
    );
    __privateAdd(this, _form);
    __privateAdd(this, _selectedVariant);
  }
  async connectedCallback() {
    console.log("DEBUG - Connected");
    __privateSet(
      this,
      _selectedVariant,
      JSON.parse(
        this.querySelector("script[data-variant]")?.textContent || "{}"
      )
    );
    __privateSet(this, _form, document.forms[this.getAttribute("form-id")]);
    __privateGet(this, _delegate).on(
      "change",
      `input[data-option-position][form="${this.getAttribute("form-id")}"]`,
      __privateMethod(this, _VariantPicker_instances, onOptionChanged_fn).bind(
        this
      )
    );
    __privateGet(this, _delegate).on(
      "pointerenter",
      `input[data-option-position][form="${this.getAttribute(
        "form-id"
      )}"]:not(:checked) + label`,
      __privateMethod(this, _VariantPicker_instances, onOptionPreload_fn).bind(
        this
      ),
      true
    );
    __privateGet(this, _delegate).on(
      "touchstart",
      `input[data-option-position][form="${this.getAttribute(
        "form-id"
      )}"]:not(:checked) + label`,
      __privateMethod(this, _VariantPicker_instances, onOptionPreload_fn).bind(
        this
      ),
      true
    );
    __privateGet(this, _intersectionObserver).observe(this);
  }
  disconnectedCallback() {
    console.log("DEBUG - Disconnected");
    __privateGet(this, _delegate).off();
    __privateGet(this, _intersectionObserver).unobserve(this);
  }
  get selectedVariant() {
    return __privateGet(this, _selectedVariant);
  }
  get productHandle() {
    return this.getAttribute("handle");
  }
  get updateUrl() {
    return this.hasAttribute("update-url");
  }
  /**
   * Select a variant using a list of option values. The list of option values might lead to no variant (for instance)
   * in the case of a combination that does not exist
   */
  async selectCombination({ optionValues, productChange }) {
    const previousVariant = this.selectedVariant;
    const newContent = document
      .createRange()
      .createContextualFragment(
        await __privateMethod(
          this,
          _VariantPicker_instances,
          renderForCombination_fn
        ).call(this, optionValues)
      );
    if (!productChange) {
      const newVariantPicker = deepQuerySelector(
        newContent,
        `${this.tagName}[form-id="${this.getAttribute("form-id")}"]`
      );
      const newVariant = JSON.parse(
        newVariantPicker.querySelector("script[data-variant]")?.textContent ||
          "{}"
      );
      __privateSet(this, _selectedVariant, newVariant);
      __privateGet(this, _form).id.value = __privateGet(
        this,
        _selectedVariant
      )?.id;
      __privateGet(this, _form).id.dispatchEvent(
        new Event("change", { bubbles: true })
      );
      if (this.updateUrl && __privateGet(this, _selectedVariant)?.id) {
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.set(
          "variant",
          __privateGet(this, _selectedVariant).id
        );
        window.history.replaceState(
          { path: newUrl.toString() },
          "",
          newUrl.toString()
        );
      }
      __privateGet(this, _form).dispatchEvent(
        new CustomEvent("variant:change", {
          bubbles: true,
          detail: {
            formId: __privateGet(this, _form).id,
            variant: __privateGet(this, _selectedVariant),
            previousVariant,
          },
        })
      );
    }
    __privateGet(this, _form).dispatchEvent(
      new CustomEvent("product:rerender", {
        detail: {
          htmlFragment: newContent,
          productChange,
        },
      })
    );
    Shopify?.PaymentButton?.init();
  }
};
_preloadedHtml = new WeakMap();
_delegate = new WeakMap();
_intersectionObserver = new WeakMap();
_form = new WeakMap();
_selectedVariant = new WeakMap();
_VariantPicker_instances = new WeakSet();
/**
 * Get the option values for the active combination
 */
getActiveOptionValues_fn = function () {
  return Array.from(__privateGet(this, _form).elements)
    .filter((item) => item.matches("input[data-option-position]:checked"))
    .sort(
      (a, b) =>
        parseInt(a.getAttribute("data-option-position")) -
        parseInt(b.getAttribute("data-option-position"))
    )
    .map((input) => input.value);
};
/**
 * Get the option values for a given input
 */
getOptionValuesFromOption_fn = function (input) {
  const optionValues = [
    input,
    ...Array.from(__privateGet(this, _form).elements).filter((item) =>
      item.matches(
        `input[data-option-position]:not([name="${input.name}"]):checked`
      )
    ),
  ]
    .sort(
      (a, b) =>
        parseInt(a.getAttribute("data-option-position")) -
        parseInt(b.getAttribute("data-option-position"))
    )
    .map((input2) => input2.value);
  return optionValues;
};
onOptionChanged_fn = async function (event) {
  if (!event.target.name.includes("option")) {
    return;
  }
  this.selectCombination({
    optionValues: __privateMethod(
      this,
      _VariantPicker_instances,
      getActiveOptionValues_fn
    ).call(this),
    productChange: event.target.hasAttribute("data-product-url"),
  });
};
/**
 * To improve the user experience, we preload a variant whenever the user hovers over a specific option
 */
onOptionPreload_fn = function (event, target) {
  __privateMethod(this, _VariantPicker_instances, renderForCombination_fn).call(
    this,
    __privateMethod(
      this,
      _VariantPicker_instances,
      getOptionValuesFromOption_fn
    ).call(this, target.control)
  );
};
/**
 * When the variant picker is intersecting the viewport, we preload the options to improve the user experience
 * so that switching variants is nearly instant
 */
onIntersection_fn = function (entries) {
  const prerenderOptions = () => {
    Array.from(__privateGet(this, _form).elements)
      .filter((item) =>
        item.matches("input[data-option-position]:not(:checked)")
      )
      .forEach((input) => {
        __privateMethod(
          this,
          _VariantPicker_instances,
          renderForCombination_fn
        ).call(
          this,
          __privateMethod(
            this,
            _VariantPicker_instances,
            getOptionValuesFromOption_fn
          ).call(this, input)
        );
      });
  };
  if (entries[0].isIntersecting) {
    if (window.requestIdleCallback) {
      window.requestIdleCallback(prerenderOptions, { timeout: 2e3 });
    } else {
      prerenderOptions();
    }
  }
};
renderForCombination_fn = async function (optionValues) {
  const optionValuesAsString = optionValues.join(",");
  const hashKey = __privateMethod(
    this,
    _VariantPicker_instances,
    createHashKeyForHtml_fn
  ).call(this, optionValuesAsString);
  let productUrl = `${Shopify.routes.root}products/${this.productHandle}`;
  for (const optionValue of optionValues) {
    const inputForOptionValue = Array.from(
      __privateGet(this, _form).elements
    ).find((item) => item.matches(`input[value="${optionValue}"]`));
    if (inputForOptionValue?.dataset.productUrl) {
      productUrl = inputForOptionValue.dataset.productUrl;
      break;
    }
  }
  if (!__privateGet(_VariantPicker, _preloadedHtml).has(hashKey)) {
    const sectionQueryParam =
      this.getAttribute("context") === "quick_buy"
        ? ""
        : `&section_id=${this.getAttribute("section-id")}`;
    const promise = new Promise(async (resolve) => {
      resolve(
        await (
          await fetch(
            `${productUrl}?option_values=${optionValuesAsString}${sectionQueryParam}`
          )
        ).text()
      );
    });
    __privateGet(_VariantPicker, _preloadedHtml).set(hashKey, {
      htmlPromise: promise,
      timestamp: Date.now(),
    });
    if (__privateGet(_VariantPicker, _preloadedHtml).size > 100) {
      __privateGet(_VariantPicker, _preloadedHtml).delete(
        Array.from(__privateGet(_VariantPicker, _preloadedHtml).keys())[0]
      );
    }
  }
  return __privateGet(_VariantPicker, _preloadedHtml).get(hashKey).htmlPromise;
};
createHashKeyForHtml_fn = function (optionValuesAsString) {
  return `${optionValuesAsString}-${this.getAttribute("section-id")}`;
};
__privateAdd(_VariantPicker, _preloadedHtml, /* @__PURE__ */ new Map());
var VariantPackSelector = _VariantPicker;
if (!window.customElements.get("variant-pack-selector")) {
  window.customElements.define("variant-pack-selector", VariantPackSelector);
}

var PackSelector = class extends HTMLElement {
  connectedCallback() {
    this.tplPrice = this.dataset.tplPrice || '{price}';
    this.tplDiscount = this.dataset.tplDiscount || '';
    this.currencySymbol = this.dataset.currencySymbol || 'R';
    this.initialBasePrice = parseInt(this.dataset.initialPrice) || 0;
    this.tabs = this.querySelectorAll('.pack-tab');

    this.formQuantityInput = document.getElementById(`${this.dataset.productFormId}-quantity`);

    this.addEventListener('quantity-pack-change', this.onPackChange.bind(this));

    const defaultChecked = this.querySelector('input[name="quantity-selection"]:checked');
    if (defaultChecked && this.formQuantityInput) {
      this.formQuantityInput.value = defaultChecked.value;
    }

    this.boundOnVariantChange = this.onVariantChange.bind(this);
    this.closest('body').addEventListener('variant:change', this.boundOnVariantChange);

    this.updateAllPrices(this.initialBasePrice);
  }

  disconnectedCallback() {
    this.closest('body').removeEventListener('variant:change', this.boundOnVariantChange);
  }

  onPackChange(event) {
    if (this.formQuantityInput) {
      this.formQuantityInput.value = event.detail.quantity;
    }
  }

  onVariantChange(event) {
    const variant = event.detail.variant;
    const newPrice = (variant && variant.price) ? variant.price : 0;
    this.updateAllPrices(newPrice);
  }

  updateAllPrices(base_cents) {
    if (isNaN(base_cents)) { base_cents = 0; }

    this.tabs.forEach(label => {
      const subheadingEl = label.querySelector('.tab-subheading');
      const inputId = label.getAttribute('for');
      const inputEl = this.querySelector(`#${inputId}`);

      if (!subheadingEl || !inputEl) return;

      const packSize = parseInt(inputEl.value) || 1;
      const discountPct = parseInt(inputEl.dataset.discount) || 0;

      const m = 100 - discountPct;
      const p_raw = (base_cents * m) / 100;
      const p_up_single = Math.ceil(p_raw);
      const s_raw = (base_cents - p_up_single) * packSize;
      const s_down = Math.floor(s_raw);
      
      const price_rounded = (discountPct === 0) ? p_up_single : (Math.ceil(p_up_single / 100) * 100);
      const saving_rounded = (discountPct === 0) ? s_down : (Math.floor(s_down / 100) * 100);
      
      const priceStr = this.formatMoney(price_rounded);
      const savingStr = this.formatMoney(saving_rounded);

      let priceSub = '';
      let discountSub = '';
      if (discountPct === 0) {
        priceSub = this.tplPrice.replace('{price}', priceStr).replace('{discount}', savingStr);
        if (this.dataset.tplPrice === '{price}') {
          priceSub = priceStr;
        }
      } else {
        priceSub = this.tplPrice.replace('{price}', priceStr).replace('{discount}', savingStr);
        discountSub = this.tplDiscount.replace('{price}', priceStr).replace('{discount}', savingStr);
      }
      
      if (discountSub) {
        subheadingEl.innerHTML = `${priceSub}<br>${discountSub}`;
      } else {
        subheadingEl.innerHTML = priceSub;
      }
    });
  }

  formatMoney(cents) {
    if (typeof cents !== 'number' || isNaN(cents)) {
      cents = 0;
    }
    let value = (cents / 100).toFixed(2);
    if (value.endsWith('.00')) {
      value = (cents / 100).toFixed(0);
    }
    return `${this.currencySymbol}${value}`;
  }
};
var PackSelectorInput = class extends HTMLInputElement {
  constructor() {
    super();
    this.addEventListener("click", this._onValueChanged);
  }

  _onValueChanged(event) {
    const checkedRadio = event.target;
    const parentComponent = this.closest('pack-selector');
    if (!parentComponent) return;

    const allLabels = parentComponent.querySelectorAll('.pack-tab');
    allLabels.forEach(label => {
        label.classList.remove('active');
    });

    const labelForCheckedRadio = parentComponent.querySelector(`label[for="${checkedRadio.id}"]`);
    if (labelForCheckedRadio) {
        labelForCheckedRadio.classList.add('active');
    }

    const newQuantity = checkedRadio.value;
    const discount = checkedRadio.dataset.discount;
    this.dispatchEvent(new CustomEvent('quantity-pack-change', {
      bubbles: true,
      composed: true,
      detail: { quantity: newQuantity, discount: discount }
    }));
  }
};
if (!window.customElements.get("pack-selector")) {
  window.customElements.define("pack-selector", PackSelector);
}
if (!window.customElements.get("pack-selector-input")) {
  window.customElements.define("pack-selector-input", PackSelectorInput, { extends: "input"}); 
}
